'use client';

import { Popover, type PopoverTriggerProps } from '@base-ui/react/popover';

// VirtualElement type from Base UI's internal floating-ui integration
type VirtualElement = {
  getBoundingClientRect: () => DOMRect | ClientRect;
  contextElement?: Element;
};

import clsx from 'clsx';
import type { CSSProperties, ReactNode, Ref, RefObject } from 'react';

import { useDialogPortalContext } from '@repo/sds/components/_internal/SDSInternalDialog/dialog-portal-context';

import '@repo/sds/components/SDSPopover/SDSPopover.scss';

/** Portal target shape mirrored from Base UI's `FloatingPortal`. */
type SDSPopoverContainer = HTMLElement | ShadowRoot | null | RefObject<HTMLElement | ShadowRoot | null>;

export type SDSPopoverChangeEventDetails = Popover.Root.ChangeEventDetails;

export interface SDSPopoverProps {
  children?: ReactNode;
  content: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean, eventDetails: SDSPopoverChangeEventDetails) => void;

  // Custom anchor (for virtual positioning)
  anchor?: Element | null | VirtualElement | React.RefObject<Element | null> | (() => Element | VirtualElement | null);

  // Positioning
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  alignOffset?: number;

  // Styling
  className?: string;
  contentClassName?: string;
  style?: CSSProperties;
  contentStyle?: CSSProperties;
  /** Applied to Base UI `Popover.Positioner` (`role="presentation"` wrapper). */
  positionerClassName?: string;
  positionerStyle?: CSSProperties;

  // Behavior
  modal?: boolean;

  // Arrow
  showArrow?: boolean;
  arrowClassName?: string;

  // Trigger
  renderTrigger?: PopoverTriggerProps['render'];

  dataTestId?: string;
  triggerDataTestId?: string;

  triggerProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;

  /**
   * Optional portal target for the popover content. When unset, the popover
   * portals into the nearest enclosing `SDSInternalDialog`'s sibling portal
   * slot (via `DialogPortalContext`) if one exists, otherwise into `document.body`.
   * Pass an explicit value to override (e.g., portal into a Shadow DOM root
   * or a custom layout slot).
   */
  container?: SDSPopoverContainer;
  ref?: Ref<HTMLDivElement>;
}

export function SDSPopover({
  children,
  content,
  open,
  onOpenChange,
  anchor,
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  alignOffset = 0,
  className,
  contentClassName,
  style,
  contentStyle,
  positionerClassName,
  positionerStyle,
  modal = false,
  showArrow = false,
  arrowClassName,
  renderTrigger,
  dataTestId,
  triggerDataTestId,
  triggerProps,
  container,
  ref,
}: SDSPopoverProps) {
  const contentClasses = clsx('SDSPopover', contentClassName);
  const arrowClasses = clsx('SDSPopover__arrow', arrowClassName);
  const triggerTestIdProps = triggerDataTestId ? { 'data-testid': triggerDataTestId } : undefined;

  // Resolve portal target: explicit prop > enclosing dialog's popover slot
  // > Base UI default (body). Without the dialog fallback, a popover mounted
  // inside SDSDialog renders invisibly behind Base UI's
  // modal-Dialog backdrop.
  const dialogPortal = useDialogPortalContext();
  const portalContainer = container ?? dialogPortal?.portalContainerRef;

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      {children && (
        <Popover.Trigger
          className={className}
          style={style}
          render={renderTrigger}
          {...triggerTestIdProps}
          {...triggerProps}
        >
          {children}
        </Popover.Trigger>
      )}

      <Popover.Portal container={portalContainer}>
        <Popover.Positioner
          anchor={anchor}
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          className={positionerClassName}
          style={positionerStyle}
        >
          <Popover.Popup ref={ref} className={contentClasses} style={contentStyle} data-testid={dataTestId}>
            {showArrow && <Popover.Arrow className={arrowClasses} />}
            {content}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
